import axios from './axiosConfig'

export interface ShiftVault {
 id: string
 branch_id?: string
 name: string
 is_active: boolean
 balance?: string
}

export interface ActiveShift {
 id: string
 mode?: 'legacy_shared_drawer' | 'multi_register'
 shift_no?: number | null
 user_id: number
 /** When a manager opened the shift on behalf of the cashier (audit). */
 opened_by?: number | null
 branch_id: string
 vault_id: string
 vault?: ShiftVault
 branch?: { id: string; name: string; code?: string } | null
 opened_at: string
 closed_at?: string | null
 starting_cash: string
 opening_cash_source?: 'vault' | 'manual' | null
 drawer_ledger_enabled?: boolean
 /** Present while status is open: estimated cash in drawer (matches shift summary logic). */
 expected_cash?: string | null
 status: string
 notes?: string | null
 print_sequence_start?: number | null
 print_sequence_last?: number | null
 actual_cash?: string | null
}

export interface CurrentMeta {
 has_open_shift: boolean
 reason: string | null
 shift_owner_id: number | null
 shift_owner_name: string | null
 opened_by_id: number | null
 opened_by_name: string | null
 is_shift_owner: boolean
 can_close_shift: boolean
}

type CurrentRes = {
 status: string
 data: ActiveShift | null
 message?: string
 meta?: CurrentMeta
}

export interface ShiftListRow {
 id: string
 shift_no?: number | null
 user_id: number
 branch_id: string
 vault_id: string
 user?: { id: number; name: string } | null
 branch?: { id: string; name: string; code: string } | null
 vault?: { id: string; name: string; is_active: boolean; balance?: string } | null
 opened_at: string
 closed_at?: string | null
 starting_cash: string
 opening_cash_source?: 'vault' | 'manual' | null
 drawer_ledger_enabled?: boolean
 status: string
 notes?: string | null
 /** Open shift: expected cash in drawer; closed: counted cash at close when recorded. */
 drawer_balance?: string | null
 mode?: 'legacy_shared_drawer' | 'multi_register'
 session_count?: number
 register_count?: number
 cashier_count?: number
 open_session_count?: number
 counted_session_count?: number
 is_count_complete?: boolean
 total_shortage?: string | null
 total_overage?: string | null
 net_variance?: string | null
 has_individual_variances?: boolean
}

export interface ShiftFilterUser {
 id: number
 name: string
}

type FilterUsersRes = { status: string; data: ShiftFilterUser[]; message?: string }

type ListRes = {
 status: string
 message?: string
 data: ShiftListRow[]
 pagination?: {
 total: number
 per_page: number
 current_page: number
 last_page: number
 }
}
type OneRes = { status: string; data: ActiveShift; message?: string }

export type ShiftCloseBlocker = {
 code: string
 message: string
}

export interface ClosePreview {
 starting_cash: string
 expected_cash: string
 expected_is_negative?: boolean
 requires_close_reason?: boolean
 allowed_settlement_directions?: Array<'deposit' | 'withdraw'>
 default_settlement_direction?: 'deposit' | 'withdraw' | null
 cash_difference?: string | null
 counted_cash?: string | null
 close_blockers?: ShiftCloseBlocker[]
 can_close?: boolean
 card_payments?: string
 instapay_payments?: string
  electronic_wallet_payments?: string
  bank_transfer_payments?: string
  payment_gateway_payments?: string
 wallet_payments?: string
 credit_payments?: string
 layaway_payments?: string
 debt_collections?: string
 layaway_collections?: string
 driver_cod_collections?: string
 customer_wallet_deposits?: string
 customer_wallet_withdrawals?: string
 is_count_complete?: boolean | null
 settlement_policy?: 'manual_cash_drop' | string | null
}

export interface ShiftDetailedSummary {
 mode?: 'legacy_shared_drawer' | 'multi_register'
 close_blockers?: ShiftCloseBlocker[]
 can_close?: boolean
 shift: {
 id: string
 shift_no: number | null
 branch: { id: string; name: string; code?: string } | null
 cashier: { id: number; name: string } | null
 vault: { id: string; name: string } | null
 opened_at: string | null
 closed_at: string | null
 status: string
 starting_cash: string
 opening_cash_source?: 'vault' | 'manual' | null
 drawer_ledger_enabled?: boolean
 accounting_model?: 'shift_drawer_ledger' | 'legacy_vault_shift'
 accounting_model_note?: string | null
 }
 totals: {
 invoice_count: number
 gross_sales: string
 total_paid: string
 uncollected_sales?: string
 uncollected_invoice_count?: number
 uncollected_delivery_sales?: string
 uncollected_failed_delivery_sales?: string
 uncollected_pending_delivery_sales?: string
 uncollected_other_sales?: string
 overpaid_sales?: string
 net_payment_gap?: string
 shift_total_refunds?: string
 total_refunds: string
 refund_count: number
 cash_method_refunds?: string
 allocated_cash_refunds?: string
 non_cash_refunds?: string
 unallocated_refunds?: string
 cross_shift_refund_count?: number
 cross_shift_refund_amount?: string
 shift_net_sales_activity?: string
 net_revenue: string
 total_expenses: string
 non_cash_expenses?: string
 cash_sales: string
 delivery_settlement_sales?: string
 non_cash_sales: string
 settled_non_cash_sales?: string
 deferred_sales?: string
 classified_sales_total?: string
 sales_classification_difference?: string
 unclassified_sales?: string
 overclassified_sales?: string
 card_payments?: string
 instapay_payments?: string
  electronic_wallet_payments?: string
  bank_transfer_payments?: string
  payment_gateway_payments?: string
 wallet_payments?: string
 credit_payments?: string
 layaway_payments?: string
 debt_collections?: string
 layaway_collections?: string
 driver_cod_collections?: string
 customer_wallet_deposits?: string
 customer_wallet_withdrawals?: string
 /** @deprecated Use drawer_cash_refund_outflows */
 cash_refunds: string
 drawer_cash_refund_outflows?: string
 cash_deposits: string
 cash_withdrawals: string
 cash_drops_to_vault?: string
 cash_expenses: string
 closing_deposit?: string
 expected_cash: string
 current_expected_cash?: string | null
 original_close_expected?: string | null
 post_close_cash_delta?: string
 actual_cash: string | null
 variance: string | null
 effective_variance?: string | null
 original_close_variance?: string | null
 needs_reconciliation_review?: boolean
 deposit_amount?: string | null
 deposit_vault_id?: string | null
 closing_vault_settlement_direction?: 'deposit' | 'withdraw' | null
 data_quality_warnings?: string[]
 allocation_drawer_mismatches?: Array<{
  code: string
  shift_id: string
  refund_ids: Array<number | string>
  allocated_cash_amount: string
  /** Cash allocation that explicitly expects a physical drawer outflow. */
  expected_drawer_cash_amount?: string
  drawer_cash_refund_outflow: string
  difference: string
  allocation_status: string
  evidence_source: string
  recommended_action: string
  /** Cash refund paid from a vault rather than the shift drawer. */
  vault_routed_cash_amount?: string
  /** Legacy allocation line that does not preserve its payout source. */
  historical_unattributed_cash_amount?: string
 }>
 }
 branch_totals?: {
  opening?: string
  cash_sales: string
  cash_refunds: string
  expenses: string
  cash_in: string
  cash_out: string
  cash_drops: string
  debt_cash?: string
  layaway_cash?: string
  wallet_deposits?: string
  wallet_withdrawals?: string
  cod_cash?: string
  expected?: string
  expected_cash: string
  current_expected_cash?: string | null
  original_close_expected?: string | null
  post_close_cash_delta?: string
  counted?: string | null
  counted_cash: string | null
  partial_counted_cash?: string
  closed_expected_cash?: string
  net_variance: string | null
  partial_variance?: string
  total_shortage: string | null
  total_overage: string | null
  partial_total_shortage?: string
  partial_total_overage?: string
  is_count_complete?: boolean
  open_session_count?: number
  counted_session_count?: number
  settlement_policy?: 'manual_cash_drop' | string
  has_individual_variances: boolean
  needs_reconciliation_review?: boolean
 }
 has_individual_variances?: boolean
 sessions?: Array<{
  id: string
  uuid?: string
  session_id?: string
  status: string
  cashier: { id: number | null; name: string | null }
  cashier_id?: number | null
  cashier_name?: string | null
  register: { id?: string | null; uuid?: string | null; code: string | null; name: string | null }
  register_id?: string | null
  register_code?: string | null
  register_name?: string | null
  drawer: { financial_account_id: string | null; name: string | null; account_type: string | null }
  drawer_id?: string | null
  drawer_name?: string | null
  opened_by?: { id: number | null; name: string | null } | null
  closed_by?: { id: number | null; name: string | null } | null
  opened_at: string | null
  closed_at: string | null
  close_reason?: string | null
  opening_cash: string
  expected_cash: string
  expected_cash_snapshot?: string | null
  original_close_expected?: string | null
  current_expected_cash?: string | null
  reconciliation_expected_cash?: string | null
  counted_cash: string | null
  effective_counted_cash?: string | null
  variance: string | null
  raw_variance?: string | null
  effective_variance?: string | null
  original_close_variance?: string | null
  shortage: string
  overage: string
  variance_result?: 'shortage' | 'overage' | 'exact' | null
  needs_reconciliation_review: boolean
  review_state?: string
  reconciliation_state?: string
  late_sync_state?: string
  late_sync_cash_delta?: string
  post_close_cash_delta?: string
  cash_handoff?: {
   to_session_id?: string | null
   mode?: string | null
   amount: string
   responsibility_amount?: string
   affects_expected_cash?: boolean
  } | null
  metrics: {
   cash_sales: string
   cash_refunds: string
   expenses: string
   cash_in: string
   cash_out: string
   cash_drops: string
   [key: string]: string | boolean
  }
 }>
 invoices: Array<{
 id: number
 invoice_number: string | null
 print_sequence: number | null
 created_at: string
 customer_name: string | null
 payment_type: string
 total: string
 paid: string
 remaining_due?: string
 overpaid_amount?: string
 collection_channel?: 'delivery_settlement' | 'other'
 collection_reason?: 'delivery_failed' | 'delivery_pending' | 'delivery_partial' | 'delivery_settlement' | 'other'
 delivery_status?: string | null
 delivery_collection_status?: string | null
 delivery_amount_to_collect?: string | null
 delivery_collected_amount?: string | null
 shift_refund_amount?: string
 lifetime_refunded_amount?: string
 net_invoice_amount?: string
 refund_amount: string
 net_amount: string
 status: string
 cashier_name: string | null
 }>
 uncollected_invoices?: Array<{
 id: number
 invoice_number: string | null
 print_sequence: number | null
 created_at: string
 customer_name: string | null
 payment_type: string
 total: string
 paid: string
 remaining_due?: string
 overpaid_amount?: string
 collection_channel?: 'delivery_settlement' | 'other'
 collection_reason?: 'delivery_failed' | 'delivery_pending' | 'delivery_partial' | 'delivery_settlement' | 'other'
 delivery_status?: string | null
 delivery_collection_status?: string | null
 delivery_amount_to_collect?: string | null
 delivery_collected_amount?: string | null
 lifetime_refunded_amount?: string
 net_invoice_amount?: string
 status: string
 cashier_name: string | null
 }>
 sold_products: Array<{
 product_id: number
 product_name: string
 category_name: string | null
 quantity_sold: number
 gross_amount: string
 refunded_quantity: number
 net_quantity: number
 net_amount: string
 }>
 refunds: Array<{
 id: number
 refund_number?: string | null
 sale_id: number
 invoice_number: string | null
 amount: string
 reason: string | null
 created_at: string
 completed_at?: string | null
 completed_at_source?: 'completed_at' | 'ledger_occurred_at' | 'payment_paid_at' | 'created_at_legacy'
 cashier_name: string | null
 payment_type: string | null
 payment_source_name?: string | null
 payment_methods?: string[]
 paid_amount?: string
 allocation_status?: 'complete' | 'partial' | 'missing'
 allocated_amount?: string
 unallocated_amount?: string
 data_quality_warnings?: string[]
 is_cross_shift?: boolean
 sale_shift_id?: string | null
 sale_shift_no?: number | null
 refund_shift_id?: string | null
 }>
 cross_shift_refunds?: Array<{
 id: number
 refund_number?: string | null
 sale_id: number
 invoice_number: string | null
 amount: string
 reason: string | null
 created_at: string
 completed_at?: string | null
 cashier_name: string | null
 payment_type: string | null
 payment_source_name?: string | null
 payment_methods?: string[]
 allocation_status?: 'complete' | 'partial' | 'missing'
 allocated_amount?: string
 unallocated_amount?: string
 data_quality_warnings?: string[]
 is_cross_shift?: boolean
 sale_shift_id?: string | null
 sale_shift_no?: number | null
 refund_shift_id?: string | null
 }>
 expenses: Array<{
 id: number
 category_name: string | null
 note: string | null
 amount: string
 created_at: string
 created_by_name: string | null
 vault_name: string | null
 cash_source?: 'drawer' | 'vault' | null
 payment_source_name?: string | null
 payment_methods?: string[]
 paid_amount?: string
 }>
 payment_method_activity?: Array<{
  payment_method: string
  sales: string
  refunds: string
  expenses: string
 net: string
 }>
 cash_movements: Array<{
 id: number | string
 type: string
 direction: 'in' | 'out'
 amount: string
 source?: string | null
 vault_id?: string | null
 note: string | null
 created_at: string
 register_session_id?: string | null
 pos_register_id?: string | null
 register_code?: string | null
 register_name?: string | null
 cashier_name?: string | null
 drawer_name?: string | null
 actor_name?: string | null
 is_legacy_shared_drawer?: boolean
 }>
 delivery?: {
 orders_count: number
 delivery_sales_total: string
 delivery_delivered_sales_total?: string
 driver_settlement_sales_total?: string
 cashier_delivery_sales_total?: string
 delivery_fees_total: string
 driver_orders_count: number
 cashier_orders_count: number
 cod_to_collect: string
 cod_pending_from_customers?: string
 cod_collected_driver: string
 cod_collected_from_customers?: string
 cod_received_in_drawer?: string
 cashier_collected: string
 outstanding_cod: string
 collected_during_shift: string
 cod_settled_into_drawer_during_shift?: string
 settlements_count?: number
 settlements_amount_received?: string
 settlements_driver_earnings?: string
 drawer_settlement_customer_collected?: string
 drawer_settlement_difference?: string
 unsettled_deliveries_count?: number
 branch_unsettled_deliveries_count?: number
 branch_unsettled_drivers_count?: number
 branch_unsettled_amount?: string
 per_driver: Array<{
 driver_id: string | null
 driver_name: string
 orders_count: number
 collected: string
 outstanding: string
 unsettled_count?: number
 }>
 }
 accounting_health?: {
 ok: boolean
 issue_count: number
 issues: Array<{
 branch_id: string
 shift_id: string | null
 code: string
 count: number
 message: string
 }>
 }
}

export interface AllocationDrawerDiagnostic {
 shift_id: string
 shift_no?: number | null
 branch?: string | null
 mode?: string
 summary_mismatch?: NonNullable<ShiftDetailedSummary['totals']['allocation_drawer_mismatches']>[number]
 verdict?: {
  has_mismatch: boolean
  difference?: string
  expected_drawer_cash_amount?: string
  drawer_cash_refund_outflow_used_in_warning?: string
  recommended_action?: string
  likely_causes?: string[]
  refund_ids_flagged?: number[]
 }
 refund_reports?: Array<{
  refund_id: number
  refund_number?: string | null
  total: string
  sale_invoice_number?: string | null
  sale_payment_type?: string | null
  processed_by?: string | null
  root_cause_hint: string
  payment_lines?: Array<{
   refund_payment_line_id: string
   amount: string
   classification: string
   cash_refund_source: string
   refund_method: string
   drawer_transaction_recorded: boolean
   drawer_transaction_found: boolean
  }>
 }>
 orphan_drawer_cash_refund_outflows?: Array<{
  drawer_transaction_id: string
  amount: string
  reference_id?: string | null
 }>
 multi_register_rollup_mismatch_risk?: boolean
 multi_register_note?: string | null
}

type DetailedSummaryRes = { status: string; data: ShiftDetailedSummary; message?: string }

type AllocationDrawerDiagnosticRes = { status: string; data: AllocationDrawerDiagnostic; message?: string }

type PreviewRes = { status: string; data: ClosePreview; message?: string }

export interface CashierCloseData {
 id: string
 closed_at: string
 actual_cash: string
 transfer_amount?: string
 retained_float?: string
 settlement_status?: string | null
 status: string
}

export interface AdminCloseData extends CashierCloseData {
 starting_cash: string
 expected_cash: string
 variance: string
 cash_difference?: string
 deposit_amount?: string
 deposit_vault_id?: string | null
 settlement_source_vault_id?: string | null
 settlement_destination_vault_id?: string | null
 settlement_idempotency_key?: string | null
 notes?: string | null
}

export interface ShiftClosePrintSettings {
 enabled: boolean
 cashier_printer_name?: string | null
 shift_close_font_size?: number | string | null
 shift_close_summary_type?: 'compact' | 'detailed' | null
}

type CloseRes = {
 status: string
 message?: string
 data: CashierCloseData | AdminCloseData
 /** One-time snapshot for automatic printing; it is never exposed by the cashier summary UI. */
 print_summary?: ShiftDetailedSummary | null
 print_settings?: ShiftClosePrintSettings | null
}

export const shiftsAPI = {
 filterUsers: async (params?: { branch_id?: string }) => {
 const response = await axios.get<FilterUsersRes>('/shifts/filter-users', { params })
 return response.data
 },

 current: async (branchId?: string | null) => {
 const response = await axios.get<CurrentRes>('/shifts/current', {
 params: branchId ? { branch_id: branchId } : undefined,
 })
 return response.data
 },

 list: async (params?: {
 page?: number
 per_page?: number
 status?: 'open' | 'closed' | ''
 from_date?: string
 to_date?: string
 user_id?: number
 branch_id?: string
 mode?: 'legacy_shared_drawer' | 'multi_register'
 has_variance?: boolean
 cashier_id?: number
 register_id?: string
 sort_by?: string
 sort_dir?: 'asc' | 'desc'
 }) => {
 const response = await axios.get<ListRes>('/shifts', { params })
 return response.data
 },

 open: async (payload: {
 vault_id?: string
 drawer_financial_account_id?: string
 starting_cash: number | string
 opening_cash_source?:
 | 'vault'
 | 'manual'
 | 'zero'
 | 'retained_in_drawer'
 | 'internal_transfer'
 | 'external_classified'
 opening_source_financial_account_id?: string
 opening_classification?: string
 opening_reference?: string
 prior_shift_id?: string
 for_user_id?: number
 print_sequence_start?: number
 }) => {
 const response = await axios.post<OneRes>('/shifts/open', payload)
 return response.data
 },

 previewClose: async (shiftId: string, params?: { actual_cash?: number | string }) => {
 const response = await axios.get<PreviewRes>(`/shifts/${shiftId}/preview-close`, { params })
 return response.data
 },

 close: async (shiftId: string, payload: {
 actual_cash?: number | string
 deposit_amount?: number | string
 deposit_vault_id?: string
 deposit_financial_account_id?: string
 vault_settlement_direction?: 'deposit' | 'withdraw'
 notes?: string
 }) => {
 const response = await axios.post<CloseRes>(`/shifts/${shiftId}/close`, payload)
 return response.data
 },

 reverseSettlement: async (shiftId: string, payload: { reason: string }) => {
 const response = await axios.post(`/shifts/${shiftId}/settlement/reverse`, payload)
 return response.data
 },

 resettle: async (shiftId: string, payload: {
 deposit_amount: number | string
 deposit_vault_id?: string
 deposit_financial_account_id?: string
 notes?: string
 reason?: string
 }) => {
 const response = await axios.post(`/shifts/${shiftId}/settlement/resettle`, payload)
 return response.data
 },

 getSummary: async (shiftId: string, params?: { branch_id?: string }) => {
 const branchId = params?.branch_id
 const response = await axios.get<DetailedSummaryRes>(`/shifts/${shiftId}/summary`, {
 params: branchId ? { branch_id: branchId } : undefined,
 })
 return response.data
 },

 getAllocationDrawerDiagnostic: async (shiftId: string, params?: { branch_id?: string }) => {
 const branchId = params?.branch_id
 const response = await axios.get<AllocationDrawerDiagnosticRes>(
 `/shifts/${shiftId}/allocation-drawer-diagnostic`,
 {
 params: branchId ? { branch_id: branchId } : undefined,
 },
 )
 return response.data.data
 },
}
